/**
 * Yup Locale Configuration
 * 
 * Configure yup's built-in i18n support using setLocale.
 * Call this once at app initialization with your translation function from next-i18next.
 * 
 * Based on yup's locale structure:
 * https://github.com/jquense/yup/blob/master/src/locale.ts
 * 
 * Follows project translation best practices:
 * @see src/.cursor/rules/translation-best-practices.mdc
 */

import { setLocale } from 'yup';
import { TFunction } from 'next-i18next';
import { LOCALE_COMMON_PATHS } from 'types/locales';

/**
 * Configure yup locale for i18n using next-i18next
 * 
 * @param t - Translation function from useTranslation(Namespaces.COMMON)
 * 
 * @example
 * import { useTranslation } from 'next-i18next';
 * import { Namespaces } from 'types/locales';
 * import { configureYupLocale } from 'ui/form';
 * 
 * function App() {
 *   const { t } = useTranslation(Namespaces.COMMON);
 *   
 *   useEffect(() => {
 *     configureYupLocale(t);
 *   }, [t]);
 * }
 * 
 * // Required translation keys in public/locales/[locale]/common.json:
 * // {
 * //   "validation": {
 * //     "required": "This field is required",
 * //     "email_invalid": "Please enter a valid email address",
 * //     "min_length": "Must be at least {{min}} characters",
 * //     "max_length": "Must be at most {{max}} characters",
 * //     "matches": "Please enter a valid format",
 * //     "phone_invalid": "Please enter a valid phone number",
 * //     "must_agree": "You must agree to continue"
 * //   }
 * // }
 * 
 * // Uses LOCALE_COMMON_PATHS constants for type safety:
 * // - LOCALE_COMMON_PATHS.VALIDATION_REQUIRED
 * // - LOCALE_COMMON_PATHS.VALIDATION_EMAIL_INVALID
 * // - LOCALE_COMMON_PATHS.VALIDATION_MIN_LENGTH
 * // - LOCALE_COMMON_PATHS.VALIDATION_MAX_LENGTH
 * // - LOCALE_COMMON_PATHS.VALIDATION_MATCHES
 * // - LOCALE_COMMON_PATHS.VALIDATION_PHONE_INVALID
 * // - LOCALE_COMMON_PATHS.VALIDATION_MUST_AGREE
 */
export function configureYupLocale(t: TFunction) {
  setLocale({
    mixed: {
      required: () => t(LOCALE_COMMON_PATHS.VALIDATION_REQUIRED, { defaultValue: 'This field is required' }),
    },
    string: {
      email: () => t(LOCALE_COMMON_PATHS.VALIDATION_EMAIL_INVALID, { defaultValue: 'Please enter a valid email address' }),
      min: ({ min }) => t(LOCALE_COMMON_PATHS.VALIDATION_MIN_LENGTH, { min, defaultValue: `Must be at least ${min} characters` }),
      max: ({ max }) => t(LOCALE_COMMON_PATHS.VALIDATION_MAX_LENGTH, { max, defaultValue: `Must be at most ${max} characters` }),
      matches: () => t(LOCALE_COMMON_PATHS.VALIDATION_MATCHES, { defaultValue: 'Please enter a valid format' }),
    },
    boolean: {
      isValue: () => t(LOCALE_COMMON_PATHS.VALIDATION_MUST_AGREE, { defaultValue: 'You must agree to continue' }),
    },
  });
}
